import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    this.#applyInstantPrice(event.target);

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };

  /** @type {{ options: string[], variants: { id: number, price: number, compare: number, options: string[] }[] } | null | undefined} */
  #variantPrices;

  /**
   * Bridge Custom: show the selected variant's price at once from embedded JSON;
   * the section fetch still replaces the price block when it returns.
   * @param {Element} picker - The variant picker that dispatched the event.
   */
  #applyInstantPrice(picker) {
    if (this.#variantPrices === undefined) {
      const script = this.querySelector('script[data-bc-variant-prices]');
      try {
        this.#variantPrices = script?.textContent ? JSON.parse(script.textContent) : null;
      } catch {
        this.#variantPrices = null;
      }
    }
    const data = this.#variantPrices;
    const getSelected = /** @type {any} */ (picker).getAllSelectedOptions;
    if (!data || typeof getSelected !== 'function') return;

    /** @type {{ name: string, value: string }[]} */
    const selected = getSelected.call(picker);
    const wanted = data.options.map((name) => selected.find((option) => option.name === name)?.value);
    if (wanted.some((value) => value == null)) return;
    const variant = data.variants.find((row) => row.options.every((value, index) => value === wanted[index]));
    if (!variant) return;

    const container = this.refs.priceContainer;
    const intEl = container?.querySelector('.bc-price__int');
    const centsEl = container?.querySelectorAll('.bc-price__sup')[1];
    if (!container || !intEl || !centsEl) return;
    const hasCompare = variant.compare > variant.price;
    // Discount badge / list price appear or disappear: leave that layout to the section fetch.
    if (hasCompare !== Boolean(container.querySelector('.bc-price__list'))) return;

    const amount = (variant.price / 100).toFixed(2);
    const [whole = '0', cents = '00'] = amount.split('.');
    intEl.textContent = Number(whole).toLocaleString('en-US');
    centsEl.textContent = `.${cents}`;

    const symbol = container.querySelector('.bc-price__sup')?.textContent?.trim() || '$';
    const format = (/** @type {number} */ value) =>
      `${symbol}${(value / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const hidden = container.querySelector('.bc-price__main > .visually-hidden');
    if (hidden?.lastChild?.nodeType === Node.TEXT_NODE) {
      hidden.lastChild.textContent = hidden.lastChild.textContent?.replace(/[^\s]+$/, format(variant.price)) ?? '';
    }
    if (hasCompare) {
      const list = container.querySelector('.bc-price__list s');
      if (list?.lastChild?.nodeType === Node.TEXT_NODE) list.lastChild.textContent = format(variant.compare);
      const pct = container.querySelector('.bc-price__pct');
      if (pct) pct.textContent = `-${Math.round(((variant.compare - variant.price) * 100) / variant.compare)}%`;
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
